import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  SignalButton,
  SignalCheckbox,
  SignalDateField,
  SignalNumberField,
  SignalTextField,
  State,
} from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { PatchDataLibraryVariableApiResponse } from '@core/api/workspace/models/patchDataLibraryVariableApiResponse';
import type { PatchDataLibraryVariableDataResponse } from '@core/api/workspace/models/patchDataLibraryVariableDataResponse';

export type EditVariableValueType = 'text' | 'number' | 'date' | 'boolean';

export interface EditVariableOverlayData {
  id: string;
  name: string;
  valueType: EditVariableValueType;
  value?: string | number | boolean | null;
}

export type EditVariableOverlayResult = PatchDataLibraryVariableDataResponse;

const VALUE_TYPE_LABELS: Record<EditVariableValueType, string> = {
  text: 'Text',
  number: 'Number',
  date: 'Date',
  boolean: 'Boolean',
};

function toIsoDateString(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseIsoDate(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

@Component({
  selector: 'app-edit-variable-overlay',
  imports: [
    SignalButton,
    SignalCheckbox,
    SignalDateField,
    SignalNumberField,
    SignalTextField,
  ],
  templateUrl: './edit-variable-overlay.html',
  styleUrl: './edit-variable-overlay.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditVariableOverlayComponent extends BaseComponent {
  private readonly userAPIService = inject(WorkspaceAPIService);
  private readonly dialogRef = inject(
    DialogRef<EditVariableOverlayResult | null>,
  );
  private readonly data = inject<EditVariableOverlayData>(DIALOG_DATA);

  readonly saveState = new State<PatchDataLibraryVariableApiResponse>();
  readonly valueType = this.data.valueType;
  readonly typeLabel = VALUE_TYPE_LABELS[this.valueType];
  readonly name = this.data.name;

  readonly textValue = signal(
    this.valueType === 'text' && typeof this.data.value === 'string'
      ? this.data.value
      : '',
  );
  readonly numberValue = signal<number | null>(
    this.valueType === 'number' && typeof this.data.value === 'number'
      ? this.data.value
      : null,
  );
  readonly dateValue = signal<Date | null>(
    this.valueType === 'date' && typeof this.data.value === 'string'
      ? parseIsoDate(this.data.value)
      : null,
  );
  readonly booleanValue = signal(
    this.valueType === 'boolean' ? this.data.value === true : false,
  );

  readonly title = computed(
    () => `Edit ${this.typeLabel.toLowerCase()} variable`,
  );

  close(): void {
    this.dialogRef.close(null);
  }

  onSave(): void {
    const value = this.resolveValue();
    if (value === undefined) {
      return;
    }

    this.executeRequest({
      state: this.saveState,
      request: this.userAPIService.patchDataLibraryVariable(this.data.id, {
        value,
      }),
      showLoader: true,
      handleError: true,
      onSuccess: (response) => {
        this.toasterService.success('Variable updated.');
        this.dialogRef.close(response.data);
      },
    });
  }

  private resolveValue(): string | number | boolean | undefined {
    switch (this.valueType) {
      case 'text': {
        const text = this.textValue().trim();
        if (!text) {
          this.toasterService.error('Enter a value.');
          return undefined;
        }
        return text;
      }
      case 'number': {
        const number = this.numberValue();
        if (number === null || number === undefined || Number.isNaN(number)) {
          this.toasterService.error('Enter a number.');
          return undefined;
        }
        return number;
      }
      case 'date': {
        const date = this.dateValue();
        if (!date) {
          this.toasterService.error('Select a date.');
          return undefined;
        }
        return toIsoDateString(date);
      }
      case 'boolean':
        return this.booleanValue();
      default:
        return undefined;
    }
  }
}
